import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import {
    type AllowedPartners,
    isAttGuidedBrowsingProfile,
    TemplateStatus,
    useExpertUniversityConfiguration,
    useExpertUniversityStore,
} from "@expert/expert-university";
import { trackFullStoryEvent } from "@expert/monitoring";
import {
    Button,
    Center,
    Container,
    Group,
    LoadingOverlay,
    Overlay,
    ScrollArea,
    SimpleGrid,
    Stack,
    Switch,
    Text,
} from "@mantine/core";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Warning } from "@/common-ui";
import { LoadingAnimation } from "@/common-ui/icons/sales";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { userCache } from "@/shared-utils/auth";
import { useFeatureFlag } from "@/shared-utils/hooks";

import type { EvaluationResultsErrorResponse, TranscriptContent } from "../api/evaluations/types";

import { EvaluationApiClient } from "../api/evaluations/evaluations-client";
import { getEvaluationResultsBySessionId, updateTrainingSession } from "../api/requests";
import { useEmployeeTrainingSessions } from "../hooks/useEmployeeTrainingSessions";
import { useLiveKitCallStartInterstitial } from "../hooks/useLiveKitCallStartInterstitial";
import { useTrainingEvaluationResults } from "../hooks/useTrainingEvaluationResults";
import { useTrainingProfiles } from "../hooks/useTrainingProfiles";
import {
    TrainingSessionChallengeResultStatus,
    TrainingSessionChallengeStatusEnum,
    type TrainingSessionEvaluationResults,
} from "../types";
import { isEvaluationTranscriptContent } from "../utils";
import AscendEvaluationPage from "./ascend/AscendEvaluationPage";
import { EvaluationRetryOverlay } from "./ascend/EvaluationRetryOverlay";
import TrainingEvaluationInterstitial from "./ascend/TrainingEvaluationInterstitial";
import classes from "./ExpertUniversityHome.module.css";
import { LaunchCallSandboxButton } from "./LaunchCallSandboxButton";
import { Sidebar } from "./Sidebar";
import TrainingEvaluationPage from "./TrainingEvaluationPage";
import { TrainingProfileTile } from "./TrainingProfileTile";

const evaluationApiClient = new EvaluationApiClient();
const PROFILE_TESTING_GROUP = "HQDOMAIN_APP-NON-VZW-APP-SOLUTO-TRAINING-ASSIST-";

export const ExpertUniversityHome: FC = () => {
    const logger = useLogger({ module: "Expert University Home" });
    const { trainingPartner: partner, setTaskInSandbox, setMostRecentSessionId } = useExpertUniversityConfiguration();
    const { callStartScreen, beginCallStart } = useLiveKitCallStartInterstitial();

    // Feature flags
    const { data: isExpertUniversityAdminUser } = useFeatureFlag<boolean>("EWP-isExpertUniversityAdminUser");

    const shouldShowInProgressProfiles =
        userCache.groups.includes(PROFILE_TESTING_GROUP) || isExpertUniversityAdminUser;
    const correlationId = crypto.randomUUID();
    const [evaluationResults, setEvaluationResults] = useState<
        TranscriptContent | EvaluationResultsErrorResponse | undefined
    >();
    const [isPending, setIsPending] = useState(false);
    const [isStartingTrainingSession, setIsStartingTrainingSession] = useState(false);
    const [showInProgressProfiles, setShowInProgressProfiles] = useState(shouldShowInProgressProfiles);
    const {
        eventBus: expertUniversitySdkEventBus,
        setReconnect,
        setSessionId,
        sessionId,
    } = useExpertUniversityStore.getState();
    setReconnect(false);

    // The ATT Guided Browsing profiles are scored by the `session/{sessionId}/evaluate` flow and shown on
    // AscendEvaluationPage; every other profile stays on the legacy listener below. Each event is
    // claimed by exactly one of the two, or the session would be evaluated twice.
    const isGuidedBrowsingProfile = useCallback(
        (profileId: string | undefined) => isAttGuidedBrowsingProfile(partner, profileId),
        [partner],
    );
    const isGuidedBrowsingProfileRef = useRef(isGuidedBrowsingProfile);
    isGuidedBrowsingProfileRef.current = isGuidedBrowsingProfile;
    const guidedBrowsingEvaluation = useTrainingEvaluationResults({
        handlesProfile: isGuidedBrowsingProfile,
        module: "Expert University Home",
        isAscend: false,
    });
    const callClosedByServer = useExpertUniversityStore((state) => state.callClosedByServer) === true;

    useEffect(() => {
        const triggerEvaluationResults = expertUniversitySdkEventBus?.on(
            "trigger-evaluation-results",
            async (event) => {
                if (isGuidedBrowsingProfileRef.current(event.profileId)) return;
                setIsPending(true);

                try {
                    const evaluationResults = await evaluationApiClient.triggerEvaluationBySessionId(event.sessionId);
                    if (isEvaluationTranscriptContent(evaluationResults)) {
                        // The freshly-triggered evaluation response may omit ChallengeId. Fall back to the
                        // profileId from the event so the updated-call-flow checklist resolves correctly.
                        evaluationResults.ChallengeId ??= event.profileId;
                        trackFullStoryEvent("Evaluation Results Received", {
                            sessionId: event.sessionId ?? sessionId,
                            profileId: event.profileId,
                            evaluationResults: evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase(),
                            behaviorsPresent: evaluationResults.EvaluationDetails.logs?.behaviors_present,
                            maxBehaviors: evaluationResults.EvaluationDetails.logs?.max_behaviors,
                        });
                        logger.info(
                            {
                                challengeStatus:
                                    evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase() ===
                                    TrainingSessionChallengeResultStatus.CLEARED
                                        ? TrainingSessionChallengeStatusEnum.COMPLETE
                                        : TrainingSessionChallengeStatusEnum.NOT_COMPLETE,
                                evaluationResults: evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase(),
                                behaviorsPresent: evaluationResults.EvaluationDetails.logs?.behaviors_present,
                                maxBehaviors: evaluationResults.EvaluationDetails.logs?.max_behaviors,
                                sessionId: event.sessionId ?? sessionId,
                                profileId: event.profileId,
                            },
                            "Triggering evaluation results",
                        );
                        await updateTrainingSession({
                            SessionID: event.sessionId,
                            ChallengeId: event.profileId,
                            ChallengeStatus:
                                evaluationResults.EvaluationDetails.Results?.toLocaleLowerCase() ===
                                TrainingSessionChallengeResultStatus.CLEARED
                                    ? TrainingSessionChallengeStatusEnum.COMPLETE
                                    : TrainingSessionChallengeStatusEnum.NOT_COMPLETE,
                            EmployeeId: userCache.employeeId,
                            SessionEndTime: new Date().toISOString(),
                        });
                    }
                    setEvaluationResults(evaluationResults);
                    setIsPending(false);
                } catch (error: any) {
                    logger.info(
                        { error: error.message, sessionId: event.sessionId ?? sessionId },
                        "Error triggering evaluation results",
                    );
                    trackFullStoryEvent("Evaluation Results Error", {
                        sessionId: event.sessionId ?? sessionId,
                        profileId: event.profileId,
                    });
                    setEvaluationResults({
                        ErrorCode: "EVALUATION_ERROR",
                        Message: "Failed to fetch evaluation results",
                    });
                    setIsPending(false);
                }
            },
        );
        return () => {
            triggerEvaluationResults?.();
        };
        // oxlint-disable-next-line exhaustive-deps
    }, []);

    useEffect(() => {
        if (!expertUniversitySdkEventBus) {
            return;
        }
        // Sandbox launches still use the loading overlay; tile launches use the interstitial.
        const offStarting = expertUniversitySdkEventBus.on("starting-training-session", () => {
            setIsStartingTrainingSession(true);
        });
        const offStarted = expertUniversitySdkEventBus.on("training-session-started", () => {
            setIsStartingTrainingSession(false);
        });
        const offCancel = expertUniversitySdkEventBus.on("cancel-livekit-call-start", () => {
            setIsStartingTrainingSession(false);
        });
        const offFailed = expertUniversitySdkEventBus.on("failed-to-start-training-session", () => {
            setIsStartingTrainingSession(false);
        });
        return () => {
            offStarting();
            offStarted();
            offCancel();
            offFailed();
        };
    }, [expertUniversitySdkEventBus]);

    const partnerTrainingChallengeProfiles = useTrainingProfiles("asurion", partner as AllowedPartners, correlationId);
    // Exclude Ascend profiles from original EU UI (unless showing in-progress profiles)
    const trainingChallengeProfiles = partnerTrainingChallengeProfiles?.filter(
        (profile) =>
            (showInProgressProfiles || profile.status === TemplateStatus.IN_PRODUCTION) &&
            !(profile.learningBlockConfig?.block ?? profile.learningBlock),
    );
    // Only one flow holds results at a time: each event and each "View results" goes to exactly one.
    const shownEvaluationResults = guidedBrowsingEvaluation.evaluationResults ?? evaluationResults;
    const clearEvaluationResults = () => {
        setEvaluationResults(undefined);
        guidedBrowsingEvaluation.clearEvaluationResults();
    };

    // We're using the presence of evaluationResults, which determines state of this component, to flip
    // the query key for training sessions - this forces a refetch of the sessions after evals viewed.
    // Including the partner in the query key ensures that the training sessions are refetched when the partner changes.
    const viewFlag = useMemo(
        () => (shownEvaluationResults === undefined ? `home-${partner}` : "eval"),
        [shownEvaluationResults, partner],
    );
    const { data: expertTrainingSessions, isFetching: isFetchingTrainingSessions } = useEmployeeTrainingSessions(
        viewFlag,
        userCache.employeeId,
        partner as AllowedPartners,
    );

    const isLoadingResults = isPending || guidedBrowsingEvaluation.pendingReason === "loading";
    const isLoading =
        isLoadingResults || isStartingTrainingSession || (isFetchingTrainingSessions && !shownEvaluationResults);

    if (callStartScreen) {
        return callStartScreen;
    }

    if (
        guidedBrowsingEvaluation.pendingReason === "evaluating" ||
        guidedBrowsingEvaluation.pendingReason === "scoring"
    ) {
        return (
            <>
                <TrainingEvaluationInterstitial
                    phase={guidedBrowsingEvaluation.pendingReason === "scoring" ? "scoring" : "submitting"}
                    hasError={guidedBrowsingEvaluation.hasRetryableError}
                    closedByServer={callClosedByServer}
                />
                {guidedBrowsingEvaluation.hasRetryableError && (
                    <EvaluationRetryOverlay onRetry={guidedBrowsingEvaluation.retryEvaluation} />
                )}
            </>
        );
    }

    if (isLoading) {
        return (
            <LoadingOverlay
                visible={isLoading}
                loaderProps={{
                    children: (
                        <Container maw="50%">
                            <LoadingAnimation />
                            <Text>
                                {isStartingTrainingSession
                                    ? "Starting training session...it may take up to 40 seconds."
                                    : isLoadingResults
                                      ? "Loading results...it may take up to 20 seconds."
                                      : "Loading training sessions..."}
                            </Text>
                        </Container>
                    ),
                }}
                overlayProps={{
                    backgroundOpacity: 1,
                    color: "var(--exwo-color-app-bg)",
                }}
            />
        );
    }

    if (shownEvaluationResults === undefined) {
        setSessionId(undefined);
        return (
            <div className={classes.background}>
                <div className={classes.container}>
                    <div className={classes.expertUniversityColumn}>
                        <Stack gap="1.5rem" h="100%">
                            <Group gap="4">
                                <Text
                                    fw={700}
                                    c="var(--mantine-color-dark-3)"
                                    fz="1.25rem"
                                    lh="1.5rem"
                                    style={{ flex: 1 }}
                                >
                                    Select a Profile
                                </Text>
                                <LaunchCallSandboxButton />
                            </Group>
                            {shouldShowInProgressProfiles && (
                                <Switch
                                    label="Show in progress profiles"
                                    checked={showInProgressProfiles}
                                    onChange={(event) => setShowInProgressProfiles(event.currentTarget.checked)}
                                    fw={700}
                                    pl="0.5rem"
                                    c="var(--mantine-color-dark-3)"
                                />
                            )}
                            <ScrollArea
                                offsetScrollbars
                                scrollbarSize={8}
                                styles={{
                                    viewport: { paddingRight: 16 },
                                }}
                            >
                                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} mt="0.5rem" ml="0.5rem" spacing="md">
                                    {trainingChallengeProfiles?.map((profile) => {
                                        const trainingProfileSessions = expertTrainingSessions?.find(
                                            (trainingSession) => trainingSession.challengeId === profile.id,
                                        );
                                        let latestSession: TrainingSessionEvaluationResults | undefined =
                                            trainingProfileSessions?.evaluationDetails?.[0];

                                        // if there is more than one training session associated with the expert,
                                        // grab the latest session by timestamp
                                        if (
                                            trainingProfileSessions?.challengeTaken &&
                                            trainingProfileSessions?.evaluationDetails.length > 1
                                        ) {
                                            latestSession = trainingProfileSessions.evaluationDetails.reduce((a, b) => {
                                                const previousSessionTime = a.Timestamp ?? 0;
                                                const currentSessionTime = b.Timestamp ?? 0;

                                                return new Date(previousSessionTime) > new Date(currentSessionTime)
                                                    ? a
                                                    : b;
                                            });
                                        }
                                        return (
                                            <TrainingProfileTile
                                                key={profile.id}
                                                id={profile.id}
                                                description={profile.description}
                                                challengeTaken={trainingProfileSessions?.challengeTaken ?? false}
                                                challengeStatus={latestSession?.Results}
                                                onStart={async () => {
                                                    const isInLiveKitPilot = await getFeature<boolean>(
                                                        "EWP-isInLiveKitPilot",
                                                        { profile: profile.id, partner: partner as AllowedPartners },
                                                    );

                                                    trackFullStoryEvent("Expert_University_Training_Session_Started", {
                                                        profileId: profile.id,
                                                    });
                                                    if (expertUniversitySdkEventBus) {
                                                        logger.info(
                                                            { profileId: profile.id },
                                                            "CALL CREATE TRAINING TASK",
                                                        );
                                                        setTaskInSandbox(false);
                                                        if (isInLiveKitPilot) {
                                                            beginCallStart({
                                                                profileId: profile.id,
                                                                partner: partner as AllowedPartners,
                                                                isLiveKitSession: true,
                                                            });
                                                        }
                                                        expertUniversitySdkEventBus.emit("start-simulation-training", {
                                                            profileId: profile.id,
                                                            partner: partner as AllowedPartners,
                                                            liveKitConfiguration: {
                                                                isLiveKitSession: Boolean(isInLiveKitPilot),
                                                            },
                                                        });
                                                    }
                                                }}
                                                onViewResults={async () => {
                                                    if (
                                                        latestSession?.SessionId &&
                                                        isGuidedBrowsingProfile(profile.id)
                                                    ) {
                                                        setMostRecentSessionId(latestSession.SessionId);
                                                        void guidedBrowsingEvaluation.viewResultsBySessionId(
                                                            latestSession.SessionId,
                                                        );
                                                    } else if (latestSession?.SessionId) {
                                                        setIsPending(true);
                                                        const read = await getEvaluationResultsBySessionId(
                                                            latestSession.SessionId,
                                                        );
                                                        // A record can come back with no verdict on it, so
                                                        // guard before rendering.
                                                        setEvaluationResults(
                                                            read.status === "found" &&
                                                                isEvaluationTranscriptContent(read.item)
                                                                ? read.item
                                                                : undefined,
                                                        );
                                                        setIsPending(false);
                                                        setMostRecentSessionId(latestSession.SessionId);
                                                    }
                                                    trackFullStoryEvent("Expert_University_View_Results_Called", {
                                                        sessionId: latestSession?.SessionId ?? sessionId,
                                                        profileId: profile.id,
                                                    });
                                                    logger.info(
                                                        {
                                                            profileId: profile.id,
                                                            sessionId: latestSession?.SessionId ?? sessionId,
                                                        },
                                                        "CALL VIEW RESULTS",
                                                    );
                                                }}
                                            />
                                        );
                                    })}
                                </SimpleGrid>
                            </ScrollArea>
                        </Stack>
                    </div>
                    <div className={classes.expertUniversityColumn}>
                        <Sidebar partner={partner as AllowedPartners} />
                    </div>
                </div>
            </div>
        );
    }

    if (
        guidedBrowsingEvaluation.evaluationResults &&
        isEvaluationTranscriptContent(guidedBrowsingEvaluation.evaluationResults)
    ) {
        return (
            <div className={classes.background}>
                <AscendEvaluationPage
                    evaluationResults={guidedBrowsingEvaluation.evaluationResults}
                    onCompleteEvaluation={guidedBrowsingEvaluation.clearEvaluationResults}
                />
            </div>
        );
    }

    if (isEvaluationTranscriptContent(shownEvaluationResults)) {
        return (
            <div className={classes.background}>
                <TrainingEvaluationPage
                    evaluationResults={shownEvaluationResults}
                    onCompleteEvaluation={() => {
                        setEvaluationResults(undefined);
                    }}
                    partner={partner}
                />
            </div>
        );
    }

    if (shownEvaluationResults.ErrorCode) {
        return (
            <Overlay data-testid="return-home-screen" color="var(--exwo-color-app-bg)" backgroundOpacity={0.85}>
                <Center
                    style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
                    className={sharedStyles.animateScaleIn}
                >
                    <Warning width={80} height={80} />
                    <Text fw="700" ta="center" size="xl">
                        There was an issue evaluating your results, please select a profile and try again.
                    </Text>
                    <Button onClick={clearEvaluationResults}>Return Home</Button>
                </Center>
            </Overlay>
        );
    }

    return (
        <Center
            style={{ flexDirection: "column", height: "100%", gap: "1.5rem" }}
            className={sharedStyles.animateScaleIn}
        >
            <Text fw="700" ta="center" size="xl">
                Welcome to Expert University
            </Text>
        </Center>
    );
};

export default ExpertUniversityHome;
